import { Menu, User, LogOut, Settings, Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useWhiteLabelStore } from '@/stores/whiteLabelStore';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertsDropdown } from './AlertsDropdown';

interface HeaderProps {
  onMenuClick: () => void;
}

export const Header = ({ onMenuClick }: HeaderProps) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const { headerTitle } = useWhiteLabelStore();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const langMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target as Node)) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header 
      className="sticky top-0 z-40"
      style={{
        backgroundColor: '#FFF',
        borderBottom: '1px solid #E6E6F0'
      }}
    >
      <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-lg transition-colors"
            aria-label="Toggle menu"
            style={{
              color: 'var(--wl-text-muted)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex items-center gap-3">
            <h1 
              className="hidden md:block text-sm lg:text-base font-semibold"
              style={{ 
                color: 'var(--wl-primary)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
              }}
            >
              {headerTitle}
            </h1>
          </div>
        </div>

        {/* Right section */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Language Selector */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              className="p-2 rounded-lg transition-colors"
              aria-label="Change language"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <Globe 
                className="w-5 h-5"
                style={{ color: 'var(--wl-text-muted)' }}
              />
            </button>

            {isLangMenuOpen && (
              <div 
                className="absolute right-0 mt-2 w-48 py-1"
                style={{
                  backgroundColor: '#FFF',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)',
                  border: '1px solid #E6E6F0'
                }}
              >
                <button
                  onClick={async () => {
                    console.log('Changing language to: en');
                    await i18n.changeLanguage('en');
                    console.log('Language changed to:', i18n.language);
                    setIsLangMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                  style={{ 
                    color: i18n.language === 'en' ? 'var(--wl-primary)' : '#22212C',
                    fontWeight: i18n.language === 'en' ? 500 : 400
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="text-lg">🇺🇸</span>
                  English
                </button>
                
                <button
                  onClick={async () => {
                    console.log('Changing language to: pt-BR');
                    await i18n.changeLanguage('pt-BR');
                    console.log('Language changed to:', i18n.language);
                    setIsLangMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                  style={{ 
                    color: (i18n.language === 'pt-BR' || i18n.language === 'pt') ? 'var(--wl-primary)' : '#22212C',
                    fontWeight: (i18n.language === 'pt-BR' || i18n.language === 'pt') ? 500 : 400
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <span className="text-lg">🇧🇷</span>
                  Português (BR)
                </button>
              </div>
            )}
          </div>

          {/* Notifications */}
          <AlertsDropdown />

          {/* User menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-2 p-2 rounded-lg transition-colors"
              aria-label="User menu"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div 
                className="w-8 h-8 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'var(--wl-primary-light)' }}
              >
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.fullName}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <User 
                    className="w-5 h-5"
                    style={{ color: 'var(--wl-primary)' }}
                  />
                )}
              </div>
              <span 
                className="hidden sm:block text-sm font-medium"
                style={{ color: 'var(--wl-secondary)', fontWeight: 500 }}
              >
                {user?.fullName || 'User'}
              </span>
            </button>

            {/* Dropdown menu */}
            {isUserMenuOpen && (
              <div 
                className="absolute right-0 mt-2 w-56 py-1"
                style={{
                  backgroundColor: '#FFF',
                  borderRadius: '8px',
                  boxShadow: '0px 4px 8px -4px rgba(50, 71, 92, 0.42)',
                  border: '1px solid #E6E6F0'
                }}
              >
                <div 
                  className="px-4 py-3"
                  style={{ borderBottom: '1px solid #E6E6F0' }}
                >
                  <p 
                    className="text-sm font-medium"
                    style={{ color: 'var(--wl-secondary)', fontWeight: 500 }}
                  >
                    {user?.fullName}
                  </p>
                  <p 
                    className="text-xs truncate"
                    style={{ color: 'var(--wl-text-muted)' }}
                  >
                    {user?.email}
                  </p>
                </div>
                
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                  style={{ color: 'var(--wl-secondary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(50, 71, 92, 0.04)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Settings className="w-4 h-4" />
                  {t('settings.title')}
                </button>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                  style={{ color: '#E5384F' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(229, 56, 79, 0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  {t('auth.logout')}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
